import React, { useEffect, useState } from 'react';
import { getFuelRecords } from '../../services/api';
import ResponsiveTable from '../shared/ResponsiveTable';

const FuelSalesPage = ({ user }) => {
  const [records, setRecords] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  });
  const [filterType, setFilterType] = useState('date'); // 'date' or 'month'
  const [loading, setLoading] = useState(true);

  // Function to fetch data based on current filter settings
  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch records based on current filter type
      const params = filterType === 'date' 
        ? { date: selectedDate } 
        : { month: selectedMonth };
      
      console.log('Fetching fuel records with filterType:', filterType, 'and params:', params);
      const res = await getFuelRecords(params);
      console.log('Received response with', res.data?.length || 0, 'records');
      const data = res.data || [];
      setRecords(data);
    } catch (err) {
      console.error('Error fetching fuel records:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when filter type, selected date, or selected month changes
  useEffect(() => {
    fetchData();
  }, [filterType, selectedDate, selectedMonth]);

  const renderRow = (record, isMobile) => {
    if (isMobile) {
      const dateStr = new Date(record.created_at).toLocaleDateString();
      const timeStr = new Date(record.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      return (
        <div className="flex flex-col">
          <div className="flex justify-between items-center">
            <div className="text-sm font-semibold text-gray-900">{record.fuel_type}</div>
            <div className="text-sm font-medium text-gray-900">{record.quantity} L</div>
          </div>
          <div className="flex justify-between items-center mt-1 text-sm text-gray-500">
            <div>₹{record.total_amount ? parseFloat(record.total_amount).toFixed(2) : 'N/A'}</div>
            <div className="text-right">{dateStr} · {timeStr}</div>
          </div>
        </div>
      );
    }

    return (
      <>
        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{record.fuel_type}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{record.quantity} L</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{record.total_amount ? `₹${parseFloat(record.total_amount).toFixed(2)}` : 'N/A'}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{new Date(record.created_at).toLocaleDateString()}</td>
      </>
    );
  };

  const handleFilterTypeChange = (type) => {
    setFilterType(type);
  };

  const handleRefresh = () => {
    fetchData();
  };

  const handleMonthChange = (e) => {
    const newMonth = e.target.value;
    setSelectedMonth(newMonth);
  };

  return (
    <div className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <h1 className="text-2xl font-bold mb-4">My Fuel Sales</h1>
      
      {/* Filter Type Toggle */}
      <div className="mb-4 flex space-x-4">
        <button
          onClick={() => handleFilterTypeChange('date')}
          className={`px-4 py-2 rounded-md text-sm font-medium ${
            filterType === 'date'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Daily
        </button>
        <button
          onClick={() => handleFilterTypeChange('month')}
          className={`px-4 py-2 rounded-md text-sm font-medium ${
            filterType === 'month'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Monthly
        </button>
        <button
          onClick={handleRefresh}
          className="px-4 py-2 rounded-md text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300"
        >
          Refresh
        </button>
      </div>

      {/* Filter Controls */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          {filterType === 'date' ? (
            <>
              <label className="text-sm text-gray-600">Date</label>
              <input 
                type="date" 
                value={selectedDate} 
                onChange={e => setSelectedDate(e.target.value)} 
                className="border px-2 py-1 rounded" 
              />
              <button 
                onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])} 
                className="ml-2 px-3 py-1.5 text-red-600 underline hover:text-red-700 transition"
              >
                Today
              </button>
              <button 
                onClick={() => setSelectedDate(new Date(Date.parse(selectedDate) - 24*3600*1000).toISOString().split('T')[0])} 
                className="ml-2 px-2 py-1 bg-gray-100 rounded"
              >
                Prev
              </button>
              <button 
                onClick={() => setSelectedDate(new Date(Date.parse(selectedDate) + 24*3600*1000).toISOString().split('T')[0])} 
                className="ml-1 px-2 py-1 bg-gray-100 rounded"
              >
                Next
              </button>
            </>
          ) : (
            <>
              <label className="text-sm text-gray-600">Month</label>
              <input 
                type="month" 
                value={selectedMonth} 
                onChange={handleMonthChange} 
                className="border px-2 py-1 rounded" 
              />
              <button 
                onClick={() => {
                  const today = new Date();
                  setSelectedMonth(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`);
                }} 
                className="ml-2 px-3 py-1.5 text-red-600 underline hover:text-red-700 transition"
              >
                This Month
              </button>
            </>
          )}
        </div>
        <div className="text-sm text-gray-700">
          Showing <span className="font-medium">{records.length}</span> records · Total ₹<span className="font-medium">{records.reduce((s,r)=>s+(parseFloat(r.total_amount)||0),0).toFixed(2)}</span>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <ResponsiveTable
          headers={[ 'Fuel Type', 'Quantity (L)', 'Total (₹)', 'Date' ]}
          data={records}
          renderRow={renderRow}
          emptyMessage="No fuel records found"
          loading={loading}
        />
      </div>
    </div>
  );
};

export default FuelSalesPage;